class ScrollService {
    constructor() {
        this.navRef = null
        this.isNavSticky = false
        this.stickyTriggerOffset = 100
    }

    /**
     * Register a navigation element reference and sticky behavior settings.
     * @param {React.RefObject<HTMLElement>} navRef - Ref to the navigation element.
     * @param {boolean} isSticky - Whether the navigation becomes sticky.
     * @param {number} [triggerOffset=100] - Scroll offset (px) at which sticky behavior activates.
     */
    registerNav(navRef, isSticky, triggerOffset = 100) {
        this.navRef = navRef
        this.isNavSticky = isSticky
        this.stickyTriggerOffset = triggerOffset
    }

    /**
     * Attempts to move focus to a target element or its first focusable descendant.
     * Falls back to making the container temporarily focusable if needed.
     * @param {HTMLElement|null} targetElement - The element representing the section.
     * @private
     */
    _focusElement(targetElement) {
        if (!targetElement) return

        const focusableSelector = [
            'a[href]',
            'area[href]',
            'input:not([disabled]):not([type="hidden"])',
            'select:not([disabled])',
            'textarea:not([disabled])',
            'button:not([disabled])',
            'iframe',
            '[tabindex]:not([tabindex="-1"])'
        ].join(',')

        const isVisible = (el) =>
            !!(el && (el.offsetWidth || el.offsetHeight || el.getClientRects().length))

        const isFocusable = (el) => {
            if (!el) return false
            if (el.hasAttribute('tabindex')) {
                const ti = parseInt(el.getAttribute('tabindex'), 10)
                if (!Number.isNaN(ti) && ti >= 0) return true
            }
            return typeof el.matches === 'function' && el.matches(focusableSelector)
        }

        let toFocus = null
        if (isFocusable(targetElement) && isVisible(targetElement)) {
            toFocus = targetElement
        } else {
            const inner = targetElement.querySelector(focusableSelector)
            if (inner && isVisible(inner)) {
                toFocus = inner
            }
        }

        let cleanup = null
        if (!toFocus) {
            const hadTabIndex = targetElement.hasAttribute('tabindex')
            const prevTabIndex = hadTabIndex
                ? targetElement.getAttribute('tabindex')
                : null

            if (!hadTabIndex) {
                targetElement.setAttribute('tabindex', '-1')
            }

            toFocus = targetElement

            cleanup = () => {
                if (!hadTabIndex) {
                    targetElement.removeAttribute('tabindex')
                } else if (prevTabIndex !== null) {
                    targetElement.setAttribute('tabindex', prevTabIndex)
                }
                targetElement.removeEventListener('blur', cleanup)
            }

            targetElement.addEventListener('blur', cleanup)
        }

        try {
            if (typeof toFocus.focus === 'function') {
                try {
                    toFocus.focus({ preventScroll: true })
                } catch (e) {
                    toFocus.focus()
                }
            }
        } catch (err) {
            if (cleanup) {
                cleanup()
            }
        }
    }

    /**
     * Scrolls to a specific section by ID, adjusting for fixed navigation height.
     * Optionally focuses the target element.
     * @param {string} sectionId - The ID of the section element to scroll to.
     * @param {Object} [options={}] - Scrolling options.
     * @param {number} [options.additionalOffset=0] - Extra offset (px) to subtract (e.g., spacing).
     * @param {'auto'|'smooth'} [options.behavior='smooth'] - Scroll behavior.
     * @param {boolean} [options.focusElement=true] - Whether to move focus to the section.
     */
    scrollToSection(sectionId, options = {}) {
        const { additionalOffset = 0, behavior = 'smooth', focusElement = true } = options

        const targetElement = document.getElementById(sectionId)
        if (!targetElement) {
            return
        }

        const navbarHeight = this.navRef?.current?.offsetHeight || 0
        const fixedNavbarHeight = 84
        const targetRect = targetElement.getBoundingClientRect()
        const targetAbsoluteTop = targetRect.top + window.scrollY

        let scrollToPosition = targetAbsoluteTop

        /// Case 1: Nav is currently fixed - nav height is already affecting viewport
        // Just need to position section below the fixed nav
        if (this.isNavSticky) {
            scrollToPosition -= navbarHeight
        } else {
            // Case 2: Nav is not fixed yet, but might become fixed after scroll
            // Check if scrolling to target will trigger nav to become fixed
            const navWillBeFixed = targetAbsoluteTop >= this.stickyTriggerOffset

            if (navWillBeFixed) {
                // When nav becomes fixed:
                // 1. Original nav height (navbarHeight) is removed from document flow
                // 2. New fixed nav (fixedNavbarHeight) covers the top of viewport
                scrollToPosition -= navbarHeight + fixedNavbarHeight
            }
        }
        scrollToPosition -= additionalOffset
        scrollToPosition = Math.max(0, scrollToPosition)

        if (focusElement) {
            this._focusElement(targetElement)
        }

        window.scrollTo({
            top: scrollToPosition,
            behavior
        })
    }

    /**
     * Scrolls smoothly to the top of the page and focuses first jump menu button if present.
     */
    scrollToTop() {
        const jumpMenuNav = document.querySelector('.sps2-pdp_jumpMenu')
        if (jumpMenuNav) {
            const firstButton = jumpMenuNav.querySelector('button')
            if (firstButton) {
                firstButton.focus()
            }
        }
        window.scrollTo({
            top: 0,
            behavior: 'smooth'
        })
    }
}

const scrollService = new ScrollService()
export default scrollService
